<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { LogOut, Sun, Moon, Monitor } from 'lucide-vue-next'

export type UserMenuTheme = 'auto' | 'light' | 'dark'

const props = defineProps<{
    name?: string
    email?: string
    locales: { code: string; label: string }[]
    activeLocale: string
    theme?: UserMenuTheme
    themeLabel?: string
    themeOptionLabels?: { light: string; auto: string; dark: string }
}>()

const themeOptions: { value: UserMenuTheme; icon: typeof Sun }[] = [
    { value: 'light', icon: Sun },
    { value: 'auto', icon: Monitor },
    { value: 'dark', icon: Moon }
]

function themeLabelFor(value: UserMenuTheme): string {
    return props.themeOptionLabels?.[value] ?? value
}

// Initials: max 2 chars from name (first letter of each token), fall back
// to the email local-part. "Jiří Blaška" → "JB", "petr.adamek@x.cz" → "PA".
const initials = computed<string>(() => {
    const src = props.name?.trim() || (props.email ?? '').split('@')[0].replace(/[._-]+/g, ' ')
    if (!src) return '?'
    const parts = src.split(/\s+/).filter(Boolean)
    const letters = parts.slice(0, 2).map(p => p[0]?.toUpperCase() ?? '')
    return letters.join('') || src[0].toUpperCase()
})

const emit = defineEmits<{
    (e: 'logout'): void
    (e: 'set-locale', code: string): void
    (e: 'set-theme', value: UserMenuTheme): void
}>()

const open = ref(false)
const buttonRef = ref<HTMLButtonElement | null>(null)
const popupRef = ref<HTMLDivElement | null>(null)
const popupTop = ref(0)
const popupRight = ref(0)

async function updatePosition() {
    if (!buttonRef.value) return
    const rect = buttonRef.value.getBoundingClientRect()
    popupTop.value = rect.bottom + 6
    popupRight.value = window.innerWidth - rect.right
    await nextTick()
}

function toggle() {
    open.value = !open.value
    if (open.value) updatePosition()
}

function close() {
    open.value = false
}

function onLocale(code: string) {
    emit('set-locale', code)
    close()
}

function onLogout() {
    emit('logout')
    close()
}

function onDocumentClick(e: MouseEvent) {
    if (!open.value) return
    const target = e.target as Node
    const inButton = buttonRef.value?.contains(target)
    const inPopup = popupRef.value?.contains(target)
    if (!inButton && !inPopup) close()
}

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') close()
}

function onWindowChange() {
    if (open.value) updatePosition()
}

onMounted(() => {
    document.addEventListener('click', onDocumentClick)
    document.addEventListener('keydown', onKeydown)
    window.addEventListener('resize', onWindowChange)
    window.addEventListener('scroll', onWindowChange, true)
})
onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick)
    document.removeEventListener('keydown', onKeydown)
    window.removeEventListener('resize', onWindowChange)
    window.removeEventListener('scroll', onWindowChange, true)
})
</script>

<template>
    <button
        ref="buttonRef"
        type="button"
        class="text-[10px] font-bold tracking-tight text-white cursor-pointer transition-colors duration-fast border-2"
        :class="open ? 'border-teal shadow-[0_0_0_2px_rgba(0,127,116,0.15)]' : 'border-transparent hover:border-teal'"
        :style="{
            width: '28px',
            height: '28px',
            minWidth: '28px',
            minHeight: '28px',
            flex: '0 0 28px',
            borderRadius: '50%',
            padding: '0',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: '1',
            boxSizing: 'border-box',
            background: 'linear-gradient(135deg, var(--color-teal), var(--color-teal-2d))'
        }"
        :title="(name ?? email ?? '')"
        :aria-label="'Účet: ' + (name ?? email ?? '')"
        aria-haspopup="menu"
        :aria-expanded="open"
        @click.stop="toggle"
    >
        {{ initials }}
    </button>
    <Teleport to="body">
        <div
            v-show="open"
            ref="popupRef"
            class="fixed w-[260px] bg-white border border-gray-200 rounded-12 shadow-e3 overflow-hidden"
            :style="{ top: popupTop + 'px', right: popupRight + 'px', zIndex: 1000 }"
            role="menu"
        >
            <!-- Header: avatar + name/email -->
            <div class="px-3.5 py-3 border-b border-gray-200 flex items-center gap-2.5">
                <div
                    class="flex items-center justify-center text-[11px] font-bold tracking-tight text-white shrink-0"
                    :style="{
                        width: '34px',
                        height: '34px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, var(--color-teal), var(--color-teal-2d))'
                    }"
                >
                    {{ initials }}
                </div>
                <div class="flex-1 min-w-0">
                    <div v-if="name" class="text-sm font-semibold text-ink truncate">{{ name }}</div>
                    <div v-if="email" class="text-xs text-gray-500 truncate">{{ email }}</div>
                </div>
            </div>
            <!-- Locale switcher -->
            <div class="px-3 py-2 border-b border-gray-200">
                <div class="text-[10px] uppercase font-semibold text-gray-400 mb-1.5">Jazyk</div>
                <div class="flex gap-1">
                    <button
                        v-for="loc in locales"
                        :key="loc.code"
                        type="button"
                        :class="[
                            'px-2.5 py-1 text-xs rounded-4 transition-colors duration-fast',
                            activeLocale === loc.code
                                ? 'bg-teal text-white font-semibold'
                                : 'hover:bg-gray-100 text-gray-700'
                        ]"
                        @click="onLocale(loc.code)"
                    >
                        {{ loc.label }}
                    </button>
                </div>
            </div>
            <!-- Theme switcher -->
            <div v-if="theme" class="px-3 py-2 border-b border-gray-200">
                <div class="text-[10px] uppercase font-semibold text-gray-400 mb-1.5">
                    {{ themeLabel ?? 'Theme' }}
                </div>
                <div class="inline-flex bg-gray-100 border border-gray-200 rounded-8 p-[3px] gap-[3px]">
                    <button
                        v-for="opt in themeOptions"
                        :key="opt.value"
                        type="button"
                        :class="[
                            'py-1 px-2 text-xs rounded-6 cursor-pointer transition-all duration-fast flex items-center gap-1.5',
                            theme === opt.value
                                ? 'font-semibold bg-white text-teal shadow-e1'
                                : 'font-medium bg-transparent text-gray-500 hover:text-ink'
                        ]"
                        :title="themeLabelFor(opt.value)"
                        @click="emit('set-theme', opt.value)"
                    >
                        <component :is="opt.icon" class="w-3.5 h-3.5" />
                        {{ themeLabelFor(opt.value) }}
                    </button>
                </div>
            </div>
            <!-- Logout -->
            <button
                type="button"
                class="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red hover:bg-red-l transition-colors duration-fast"
                role="menuitem"
                @click="onLogout"
            >
                <LogOut class="w-4 h-4" />
                Odhlásit se
            </button>
        </div>
    </Teleport>
</template>
